/**
 * Ephemeral candidate-mapping state for the note-push flow.
 *
 * A display-side memory of what the agent has learned about mapping a
 * template's sections onto an EHR app's UI fields, keyed by
 * ``(workspaceId, templateId, appName)``. The verification panel / section chips read this to
 * show the clinician the discovered/stored mapping and let them override it.
 *
 * Lifecycle mirrors the artifact canvas — a plain module-level atom (not an
 * ``atomFamily``), scoped to the currently-open project chat and reset on chat
 * change. "Per-project" is realised by the consumer calling
 * {@link clearCandidateMappingsAtom} when the project changes (the same
 * close/chat-change reset the artifact canvas uses); a stale mapping from
 * project A can then never surface in project B.
 *
 * This store is memory only: NO persistence, NO REST calls, NO writes to any
 * backend. The durable hint store is written exclusively by the parent agent
 * via ``record_note_push_hints``; this FE state never mirrors back to it.
 *
 * Two sources feed it via the ingest atoms below — ``get_note_push_hints`` /
 * ``record_note_push_hints`` results and ``FIELD:`` lines from a read-only
 * ``run_computer_use_task`` discovery run. The harness-coupled readers that
 * parse those tool-result payloads off the timeline live with the Work harness
 * in ``@heidi/agents`` (its note-push timeline ingest), and the app binds these
 * atoms to them; keeping them there avoids a note-push → projects dependency.
 */

import type { NotePushReportField } from '@heidi/agents/contracts';
import { workspaceIdReadAtom } from '@heidi/workspace/data';
import { atom, type Getter, type Setter } from 'jotai';

/**
 * The ``get_note_push_hints`` structured result payload
 * (``result_provider_metadata.data``). Wire shape (snake_case). Only the
 * fields this store consumes are modelled; the tool also returns
 * ``unhinted_sections``, ``known_app_names`` and ``paste_into_delegation_brief``
 * which are not held here.
 */
export interface NotePushHintsData {
  template_id: string;
  app_name: string;
  template_sections: string[];
  hints: { section_heading: string; ui_targets: string[] }[];
}

/**
 * The ``record_note_push_hints`` structured result payload. Wire shape
 * (snake_case): the sections the agent just recorded to the durable store, each
 * with its confirmed field target(s). Only the fields this store consumes are
 * modelled.
 */
export interface NotePushRecordedData {
  template_id: string;
  app_name: string;
  recorded: { section_heading: string; ui_targets: string[] }[];
}

/** A stored hint in the FE display shape (camelCase). */
export interface CandidateMappingHint {
  sectionHeading: string;
  uiTargets: string[];
}

/** One template↔app candidate mapping. */
export interface CandidateMapping {
  templateId: string;
  appName: string;
  /** Section headings from the template. */
  templateSections: string[];
  /** Durable hints recorded by the agent (from ``get_note_push_hints``). */
  hints: CandidateMappingHint[];
  /** FIELD candidates discovered by the latest read-only computer-use run. */
  fieldCandidates: NotePushReportField[];
  /** User overrides: section heading → selector[]. */
  overrides: Record<string, string[]>;
}

/**
 * Stable map key for a mapping, scoped by WORKSPACE: a mapping
 * discovered under one workspace must never resolve under another. Ingest and
 * read both take the workspace from the same source (the ambient
 * `workspaceIdReadAtom`), so a workspace switch cleanly isolates (empties) the view
 * rather than leaking cross-workspace targets. `null` workspace keys under `''`.
 */
export const candidateMappingKey = (
  templateId: string,
  appName: string,
  workspaceId: string | null
): string => `${workspaceId ?? ''}::${templateId}::${appName}`;

/**
 * Candidate mappings for the currently-open project chat, keyed by
 * {@link candidateMappingKey}. Reset on chat change via
 * {@link clearCandidateMappingsAtom}.
 */
export const candidateMappingsAtom = atom<Record<string, CandidateMapping>>({});

/**
 * Key ({@link candidateMappingKey}) of the mapping the most recent
 * ``get_note_push_hints`` ingest activated. The note-push flow targets one app
 * at a time, so consumers resolve the *active* mapping for a template — not the
 * first key that happens to match — which keeps the chip and override writes on
 * the current app when a template has candidates for more than one. ``null``
 * until the first hints ingest, and reset with the store on chat change.
 */
export const activeComputerUseNotePushMappingKeyAtom = atom<string | null>(null);

/**
 * Live active mapping for `templateId`, or undefined. Workspace-isolated on READ —
 * a stale active key from a previous workspace re-derives differently and is rejected.
 */
export const selectActiveMapping = (
  state: {
    mappings: Record<string, CandidateMapping>;
    activeKey: string | null;
    workspaceId: string | null;
  },
  templateId: string
): CandidateMapping | undefined => {
  const { mappings, activeKey, workspaceId } = state;
  if (!activeKey) return undefined;
  const active = mappings[activeKey];
  if (active?.templateId !== templateId) return undefined;
  return candidateMappingKey(templateId, active.appName, workspaceId) === activeKey
    ? active
    : undefined;
};

const emptyMapping = (templateId: string, appName: string): CandidateMapping => ({
  templateId,
  appName,
  templateSections: [],
  hints: [],
  fieldCandidates: [],
  overrides: {},
});

/** Upsert one mapping in place, applying ``patch`` to the current (or empty) entry. */
const upsertMapping = (
  get: Getter,
  set: Setter,
  templateId: string,
  appName: string,
  patch: (current: CandidateMapping) => CandidateMapping
): void => {
  const key = candidateMappingKey(templateId, appName, get(workspaceIdReadAtom));
  const map = get(candidateMappingsAtom);
  const current = map[key] ?? emptyMapping(templateId, appName);
  set(candidateMappingsAtom, { ...map, [key]: patch(current) });
};

/** Point the active-mapping key at ``(templateId, appName)`` in the current workspace. */
const setActiveKey = (
  get: Getter,
  set: Setter,
  templateId: string,
  appName: string
): void => {
  set(
    activeComputerUseNotePushMappingKeyAtom,
    candidateMappingKey(templateId, appName, get(workspaceIdReadAtom))
  );
};

/**
 * Ingest a ``get_note_push_hints`` result: refresh the template sections and
 * stored hints, preserving any FIELD candidates and user overrides already
 * discovered for this mapping.
 */
export const ingestNotePushHintsAtom = atom(null, (get, set, data: NotePushHintsData) => {
  upsertMapping(get, set, data.template_id, data.app_name, (current) => ({
    ...current,
    templateSections: data.template_sections,
    hints: data.hints.map((hint) => ({
      sectionHeading: hint.section_heading,
      uiTargets: hint.ui_targets,
    })),
  }));
  setActiveKey(get, set, data.template_id, data.app_name);
});

/**
 * Ingest a ``record_note_push_hints`` result: merge the just-recorded rows into
 * the stored hints BY HEADING — a recorded row replaces an existing heading's
 * targets and a new heading is added — while preserving every other hint, the
 * template sections, FIELD candidates, and user overrides. Also sets the active
 * key. This is what un-stales the panel after the agent records (and, on reload,
 * lets the timeline replay of the record item rebuild the confirmed mapping).
 */
export const ingestNotePushRecordedAtom = atom(
  null,
  (get, set, data: NotePushRecordedData) => {
    upsertMapping(get, set, data.template_id, data.app_name, (current) => {
      const byHeading = new Map(current.hints.map((hint) => [hint.sectionHeading, hint]));
      for (const row of data.recorded) {
        byHeading.set(row.section_heading, {
          sectionHeading: row.section_heading,
          uiTargets: row.ui_targets,
        });
      }
      return { ...current, hints: [...byHeading.values()] };
    });
    setActiveKey(get, set, data.template_id, data.app_name);
  }
);

/**
 * Ingest FIELD candidates from a read-only computer-use discovery run. The
 * latest run wins — its fields replace any previously discovered set for this
 * mapping (each run is a fresh on-screen snapshot).
 */
export const ingestFieldCandidatesAtom = atom(
  null,
  (
    get,
    set,
    payload: { templateId: string; appName: string; fields: NotePushReportField[] }
  ) => {
    upsertMapping(get, set, payload.templateId, payload.appName, (current) => ({
      ...current,
      fieldCandidates: payload.fields,
    }));
  }
);

/**
 * Set (or clear) a user override for one section. An empty ``selectors`` array
 * removes the override, falling back to the stored hints / discovered fields.
 */
export const setSectionOverrideAtom = atom(
  null,
  (
    get,
    set,
    payload: {
      templateId: string;
      appName: string;
      sectionHeading: string;
      selectors: string[];
    }
  ) => {
    upsertMapping(get, set, payload.templateId, payload.appName, (current) => {
      const overrides = { ...current.overrides };
      if (payload.selectors.length === 0) {
        delete overrides[payload.sectionHeading];
      } else {
        overrides[payload.sectionHeading] = payload.selectors;
      }
      return { ...current, overrides };
    });
  }
);

/** Reset the store — call on project/chat change (mirrors the artifact reset). */
export const clearCandidateMappingsAtom = atom(null, (_get, set) => {
  set(candidateMappingsAtom, {});
  set(activeComputerUseNotePushMappingKeyAtom, null);
});
